import { useState } from 'react'
import {
  Users,
  UserPlus,
  MoreVertical,
  Mail,
  Key,
  UserX,
  Shield,
  User,
  Briefcase,
  Loader2,
  Check,
  X,
  LogIn,
  KeyRound,
  Copy,
  UserCheck,
} from 'lucide-react'
import {
  useOrganizationMembers,
  useAddMember,
  useUpdateMember,
  useRemoveMember,
  useResetMemberPassword,
  useInviteMember,
  useImpersonateUser,
  useAdminResetPassword,
  type OrganizationMember,
} from '../../hooks/use-members'
import { ConfirmModal } from '../ConfirmModal'
import { AlertModal } from '../AlertModal'

interface MembersTabProps {
  organizationId: string
}

export function MembersTab({ organizationId }: MembersTabProps) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [showPasswordModal, setShowPasswordModal] = useState<{
    email: string
    password: string
  } | null>(null)
  const [impersonating, setImpersonating] = useState<string | null>(null)

  // Modals
  const [confirmRemove, setConfirmRemove] = useState<OrganizationMember | null>(null)
  const [confirmDeactivate, setConfirmDeactivate] = useState<OrganizationMember | null>(null)
  const [confirmReactivate, setConfirmReactivate] = useState<OrganizationMember | null>(null)
  const [confirmImpersonate, setConfirmImpersonate] = useState<OrganizationMember | null>(null)
  const [confirmInvite, setConfirmInvite] = useState<OrganizationMember | null>(null)
  const [errorModal, setErrorModal] = useState<{ title: string; message: string } | null>(null)
  const [successModal, setSuccessModal] = useState<{ title: string; message: string } | null>(null)

  const { data, isLoading } = useOrganizationMembers({
    organization_id: organizationId,
    page,
    page_size: 10,
  })

  const addMutation = useAddMember()
  const updateMutation = useUpdateMember()
  const removeMutation = useRemoveMember()
  const resetPasswordMutation = useResetMemberPassword()
  const inviteMutation = useInviteMember()
  const impersonateMutation = useImpersonateUser()
  const adminResetPasswordMutation = useAdminResetPassword()

  const members = data?.data || []
  const pagination = data?.pagination

  const getRoleBadge = (role: string) => {
    const badges: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
      admin: {
        label: 'Admin',
        className: 'bg-purple-100 text-purple-700',
        icon: <Shield className="w-3 h-3" />,
      },
      professional: {
        label: 'Profissional',
        className: 'bg-blue-100 text-blue-700',
        icon: <Briefcase className="w-3 h-3" />,
      },
      assistant: {
        label: 'Assistente',
        className: 'bg-gray-100 text-gray-700',
        icon: <User className="w-3 h-3" />,
      },
    }
    const badge = badges[role] ?? badges.assistant
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${badge?.className}`}>
        {badge?.icon}
        {badge?.label}
      </span>
    )
  }

  const handleAddMember = async (formData: {
    email: string
    full_name: string
    phone?: string
    role: 'admin' | 'professional' | 'assistant'
    password?: string
  }) => {
    try {
      const result = await addMutation.mutateAsync({
        organization_id: organizationId,
        ...formData,
      })
      setShowAddModal(false)
      // If a temporaryPassword was returned, show it
      if ((result as any)?.temporaryPassword) {
        setShowPasswordModal({
          email: formData.email,
          password: (result as any).temporaryPassword,
        })
      }
    } catch (error: unknown) {
      setErrorModal({ title: 'Erro ao adicionar membro', message: (error as Error).message || 'Erro desconhecido' })
    }
  }

  const handleChangeRole = async (member: OrganizationMember, newRole: 'admin' | 'professional' | 'assistant') => {
    try {
      await updateMutation.mutateAsync({
        id: member.id,
        organization_id: organizationId,
        data: { role: newRole },
      })
      setOpenMenu(null)
    } catch (error: unknown) {
      setErrorModal({ title: 'Erro ao alterar função', message: (error as Error).message || 'Erro desconhecido' })
    }
  }

  const confirmDeactivateAction = async () => {
    if (!confirmDeactivate) return
    try {
      await updateMutation.mutateAsync({
        id: confirmDeactivate.id,
        organization_id: organizationId,
        data: { is_active: false },
      })
      setConfirmDeactivate(null)
    } catch (error: unknown) {
      setErrorModal({ title: 'Erro ao desativar', message: (error as Error).message || 'Erro desconhecido' })
      setConfirmDeactivate(null)
    }
  }

  const confirmReactivateAction = async () => {
    if (!confirmReactivate) return
    try {
      await updateMutation.mutateAsync({
        id: confirmReactivate.id,
        organization_id: organizationId,
        data: { is_active: true },
      })
      setConfirmReactivate(null)
    } catch (error: unknown) {
      setErrorModal({ title: 'Erro ao reativar', message: (error as Error).message || 'Erro desconhecido' })
      setConfirmReactivate(null)
    }
  }

  const handleResetPassword = async (member: OrganizationMember) => {
    try {
      await resetPasswordMutation.mutateAsync({ email: member.profile.email })
      setOpenMenu(null)
      setSuccessModal({ title: 'Email enviado', message: `Email de redefinição enviado para ${member.profile.email}.` })
    } catch {
      setErrorModal({ title: 'Erro', message: 'Erro ao enviar email de redefinição' })
    }
  }

  const confirmInviteAction = async () => {
    if (!confirmInvite) return
    try {
      const result = await inviteMutation.mutateAsync({
        email: confirmInvite.profile.email,
        name: confirmInvite.profile.full_name || confirmInvite.profile.email,
        organization_id: organizationId,
      })
      setConfirmInvite(null)

      if (result.temporaryPassword) {
        setShowPasswordModal({
          email: result.email,
          password: result.temporaryPassword,
        })
        setSuccessModal({
          title: 'Acesso preparado',
          message: `O email não foi enviado automaticamente para ${result.email}. Use a senha temporária exibida para compartilhar o acesso.`,
        })
        return
      }

      setSuccessModal({ title: 'Convite enviado', message: `Convite enviado para ${confirmInvite.profile.email}.` })
    } catch (error: unknown) {
      setErrorModal({ title: 'Erro ao enviar convite', message: (error as Error).message || 'Erro desconhecido' })
      setConfirmInvite(null)
    }
  }

  const confirmRemoveAction = async () => {
    if (!confirmRemove) return
    try {
      await removeMutation.mutateAsync({
        id: confirmRemove.id,
        organization_id: organizationId,
      })
      setConfirmRemove(null)
    } catch (error: unknown) {
      setErrorModal({ title: 'Erro ao remover', message: (error as Error).message || 'Erro desconhecido' })
      setConfirmRemove(null)
    }
  }

  const confirmImpersonateAction = async () => {
    if (!confirmImpersonate) return
    setConfirmImpersonate(null)
    setImpersonating(confirmImpersonate.profile_id)
    try {
      const result = await impersonateMutation.mutateAsync({ profileId: confirmImpersonate.profile_id })
      // Sempre usar session_token via rota interna
      window.open(`/auth/impersonate?token=${result.session_token}`, '_blank')
      setOpenMenu(null)
    } catch (error: unknown) {
      setErrorModal({ title: 'Erro na impersonação', message: (error as Error).message || 'Erro ao impersonar usuário' })
    } finally {
      setImpersonating(null)
    }
  }

  const handleAdminResetPassword = async (member: OrganizationMember) => {
    try {
      const result = await adminResetPasswordMutation.mutateAsync({ profileId: member.profile_id })
      setShowPasswordModal({
        email: result.email,
        password: result.temporaryPassword,
      })
      setOpenMenu(null)
    } catch (error: unknown) {
      setErrorModal({ title: 'Erro ao gerar senha', message: (error as Error).message || 'Erro ao resetar senha' })
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-visible">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Membros da Organização</h3>
          {pagination && (
            <span className="text-sm text-gray-500">({pagination.total})</span>
          )}
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Adicionar
        </button>
      </div>

      {/* Members List */}
      {isLoading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
        </div>
      ) : members.length === 0 ? (
        <div className="p-8 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nenhum membro cadastrado</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {members.map((member) => (
            <div
              key={member.id}
              className={`p-4 flex items-center justify-between hover:bg-gray-50 ${
                !member.is_active ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{member.profile.full_name}</p>
                    {!member.is_active && (
                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">Inativo</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{member.profile.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {getRoleBadge(member.role)}

                {member.profile.auth_user_id ? (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Ativo
                  </span>
                ) : (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    Pendente
                  </span>
                )}

                {/* Actions Menu */}
                <div className="relative">
                  <button
                    onClick={() => setOpenMenu(openMenu === member.id ? null : member.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {openMenu === member.id && (
                    <>
                      <div className="fixed inset-0 z-[9998]" onClick={() => setOpenMenu(null)} />
                      <div className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] py-1">
                        {/* Change Role */}
                        <div className="px-3 py-1.5 text-xs text-gray-400 font-medium uppercase tracking-wide">
                          Função
                        </div>
                        {(['admin', 'professional', 'assistant'] as const).map((role) => (
                          <button
                            key={role}
                            onClick={() => handleChangeRole(member, role)}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                              member.role === role ? 'text-teal-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {member.role === role && <Check className="w-3 h-3" />}
                            {role === 'admin' && 'Admin'}
                            {role === 'professional' && 'Profissional'}
                            {role === 'assistant' && 'Assistente'}
                          </button>
                        ))}

                        <div className="border-t border-gray-100 my-1" />

                        {/* Send Invite - only for members without auth_user_id */}
                        {!member.profile.auth_user_id && (
                          <button
                            onClick={() => { setConfirmInvite(member); setOpenMenu(null) }}
                            className="w-full px-4 py-2 text-left text-sm text-teal-600 hover:bg-teal-50 flex items-center gap-2"
                          >
                            <Mail className="w-4 h-4" />
                            Enviar Convite
                          </button>
                        )}

                        {/* Reset Password - only for members with auth_user_id */}
                        {member.profile.auth_user_id && (
                          <button
                            onClick={() => { handleResetPassword(member); setOpenMenu(null) }}
                            disabled={resetPasswordMutation.isPending}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Key className="w-4 h-4" />
                            Enviar reset por email
                          </button>
                        )}

                        {/* Admin Reset Password */}
                        {member.profile.auth_user_id && (
                          <button
                            onClick={() => { handleAdminResetPassword(member) }}
                            disabled={adminResetPasswordMutation.isPending}
                            className="w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2"
                          >
                            <KeyRound className="w-4 h-4" />
                            Definir nova senha
                          </button>
                        )}

                        {/* Impersonate */}
                        {member.profile.auth_user_id && (
                          <button
                            onClick={() => { setConfirmImpersonate(member); setOpenMenu(null) }}
                            disabled={impersonating === member.profile_id}
                            className="w-full px-4 py-2 text-left text-sm text-purple-600 hover:bg-purple-50 flex items-center gap-2"
                          >
                            {impersonating === member.profile_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <LogIn className="w-4 h-4" />
                            )}
                            Entrar como usuário
                          </button>
                        )}

                        <div className="border-t border-gray-100 my-1" />

                        {/* Reativar / Desativar */}
                        {!member.is_active ? (
                          <button
                            onClick={() => { setConfirmReactivate(member); setOpenMenu(null) }}
                            className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                          >
                            <UserCheck className="w-4 h-4" />
                            Reativar membro
                          </button>
                        ) : (
                          <button
                            onClick={() => { setConfirmDeactivate(member); setOpenMenu(null) }}
                            className="w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2"
                          >
                            <X className="w-4 h-4" />
                            Desativar
                          </button>
                        )}

                        {/* Remove */}
                        <button
                          onClick={() => { setConfirmRemove(member); setOpenMenu(null) }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <UserX className="w-4 h-4" />
                          Remover da organização
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="p-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            Página {page} de {pagination.total_pages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === pagination.total_pages}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <AddMemberModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddMember}
          isLoading={addMutation.isPending}
        />
      )}

      {/* Password Result Modal */}
      {showPasswordModal && (
        <PasswordResultModal
          email={showPasswordModal.email}
          password={showPasswordModal.password}
          onClose={() => setShowPasswordModal(null)}
        />
      )}

      {/* Confirm Modals */}
      <ConfirmModal
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={confirmRemoveAction}
        title="Remover Membro"
        message={`Remover ${confirmRemove?.profile.full_name} da organização? O perfil do usuário será mantido, mas ele perderá o acesso.`}
        confirmText="Remover"
        variant="danger"
        isLoading={removeMutation.isPending}
      />

      <ConfirmModal
        isOpen={!!confirmDeactivate}
        onClose={() => setConfirmDeactivate(null)}
        onConfirm={confirmDeactivateAction}
        title="Desativar Membro"
        message={`Desativar ${confirmDeactivate?.profile.full_name}? O usuário perderá acesso à organização temporariamente.`}
        confirmText="Desativar"
        variant="warning"
        isLoading={updateMutation.isPending}
      />

      <ConfirmModal
        isOpen={!!confirmReactivate}
        onClose={() => setConfirmReactivate(null)}
        onConfirm={confirmReactivateAction}
        title="Reativar Membro"
        message={`Reativar ${confirmReactivate?.profile.full_name}? O usuário voltará a ter acesso à organização.`}
        confirmText="Reativar"
        variant="info"
        isLoading={updateMutation.isPending}
      />

      <ConfirmModal
        isOpen={!!confirmImpersonate}
        onClose={() => setConfirmImpersonate(null)}
        onConfirm={confirmImpersonateAction}
        title="Entrar como Usuário"
        message={`Você está prestes a entrar como ${confirmImpersonate?.profile.full_name}. Uma nova aba será aberta. Esta ação será registrada.`}
        confirmText="Continuar"
        variant="warning"
        isLoading={impersonateMutation.isPending}
      />

      <ConfirmModal
        isOpen={!!confirmInvite}
        onClose={() => setConfirmInvite(null)}
        onConfirm={confirmInviteAction}
        title="Enviar Convite"
        message={`Enviar convite de acesso para ${confirmInvite?.profile.email}?`}
        confirmText="Enviar"
        variant="info"
        isLoading={inviteMutation.isPending}
      />

      <AlertModal
        isOpen={!!errorModal}
        onClose={() => setErrorModal(null)}
        title={errorModal?.title || 'Erro'}
        message={errorModal?.message || ''}
        variant="error"
      />

      <AlertModal
        isOpen={!!successModal}
        onClose={() => setSuccessModal(null)}
        title={successModal?.title || 'Sucesso'}
        message={successModal?.message || ''}
        variant="success"
      />
    </div>
  )
}

// Add Member Modal Component
function AddMemberModal({
  onClose,
  onSubmit,
  isLoading,
}: {
  onClose: () => void
  onSubmit: (data: {
    email: string
    full_name: string
    phone?: string
    role: 'admin' | 'professional' | 'assistant'
    password?: string
  }) => void
  isLoading: boolean
}) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<'admin' | 'professional' | 'assistant'>('professional')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({ email, full_name: fullName, phone: phone || undefined, role, password: password || undefined })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Adicionar Membro</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Ex: João Silva"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="email@exemplo.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Senha <span className="text-gray-400 font-normal">(opcional — se informada, cria conta imediatamente)</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={password ? 8 : undefined}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Deixe vazio para enviar convite por email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="(11) 99999-9999"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Função *</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'professional' | 'assistant')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="admin">Admin</option>
              <option value="professional">Profissional</option>
              <option value="assistant">Assistente</option>
            </select>
          </div>

          {!password && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              Sem senha: o membro ficará como <strong>Pendente</strong> até receber e aceitar o convite por email.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              Adicionar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Password Result Modal Component
function PasswordResultModal({
  email,
  password,
  onClose,
}: {
  email: string
  password: string
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textArea = document.createElement('textarea')
      textArea.value = password
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Membro Adicionado!</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-gray-900">{email}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Senha temporária</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg font-mono text-sm break-all select-all">
                {password}
              </code>
              <button
                onClick={handleCopy}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copiar senha"
              >
                {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              Informe essa senha ao usuário e peça para alterá-la no primeiro login.
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
